import React, {useEffect, useMemo, useState} from "react";
import {useShallow} from "zustand/react/shallow";
import Tooltip from "@mui/material/Tooltip";
import SettingsIcon from "@mui/icons-material/Settings";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useSavedDealershipsStore} from "../../stores/savedDealershipsStore";
import {useListingsStore} from "../../stores/listingsStore";
import {useModalsStore} from "../../stores/modalsStore";
import {DealershipsModal} from "./DealershipsModal";

// Does the saved dealer carry the make being searched? The authoritative signal
// is whether the dealer actually shows up in the current filtered results
// (`listedDealerIds`) — that already reflects the server-side make/model/year
// filter. `dealer.makes` is only a fallback for a classified carrier whose
// inventory happens to be off the current page; an unknown/empty makes list no
// longer auto-qualifies (that was letting dealers who don't sell the make — e.g.
// a Honda dealer saved from a card/map with no makes — sit above the real
// carriers).
const carriesMake = (dealer, make, listedDealerIds) => {
    if (!make) return true
    if (listedDealerIds.has(String(dealer.external_id))) return true
    const makes = dealer.makes || []
    if (makes.length === 0) return false
    const target = make.toLowerCase()
    return makes.some((m) => String(m).toLowerCase() === target)
}

const EyeOpenIcon = () => (
    <svg className="eye-open" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
)

const EyeClosedIcon = () => (
    <svg className="eye-closed" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.4 18.4 0 0 1 5.06-5.94M9.9 4.24A10.4 10.4 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <path d="M1 1l22 22"/>
    </svg>
)

// Crosshair "show only this dealer" icon — matches the eye's 15×15 / stroke-2.
const TargetIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8"/>
        <line x1="12" y1="2" x2="12" y2="5"/>
        <line x1="12" y1="19" x2="12" y2="22"/>
        <line x1="2" y1="12" x2="5" y2="12"/>
        <line x1="19" y1="12" x2="22" y2="12"/>
        <circle cx="12" cy="12" r="2"/>
    </svg>
)

// Right-hand panel on the ListingsPage listing the user's saved dealerships. Each
// row has an eye toggle to show/hide that dealer's listings in the results (see
// listingsStore.hiddenDealerIds); adding/removing dealers is done in the settings
// modal. When a make is being searched, dealers that don't carry it collapse into
// an expandable "Doesn't carry …" group.
export function SavedDealershipsPanel() {
    const {saved, fetchSaved} = useSavedDealershipsStore(
        useShallow((state) => ({
            saved: state.saved,
            fetchSaved: state.fetchSaved,
        }))
    )
    const {searchedMake, listings, hiddenDealerIds, toggleDealerVisibility, toggleShowOnlyDealer} = useListingsStore(
        useShallow((state) => ({
            searchedMake: state.filters.searchTags.make,
            listings: state.listings,
            hiddenDealerIds: state.hiddenDealerIds,
            toggleDealerVisibility: state.toggleDealerVisibility,
            toggleShowOnlyDealer: state.toggleShowOnlyDealer,
        }))
    )
    const openModal = useModalsStore((state) => state.openModal)
    const [showOthers, setShowOthers] = useState(false)

    useEffect(() => { fetchSaved() }, [fetchSaved])

    const openAddDealerships = () => {
        openModal({
            element: DealershipsModal,
            id: "DealershipsModal",
        })
    }

    // Set of dealer ids present in the current filtered results (reused, no extra
    // request). Rebuilt only when the listings change.
    const listedDealerIds = useMemo(
        () => new Set((listings || []).map((l) => String(l?.dealership?.id)).filter((x) => x && x !== 'undefined')),
        [listings]
    )

    const matching = searchedMake ? saved.filter((d) => carriesMake(d, searchedMake, listedDealerIds)) : saved
    const others = searchedMake ? saved.filter((d) => !carriesMake(d, searchedMake, listedDealerIds)) : []

    // Target active state, derived not stored: the sole visible saved dealer.
    const visibleSavedIds = saved
        .map((d) => String(d.external_id))
        .filter((id) => !hiddenDealerIds.includes(id))
    const onlyVisibleId = visibleSavedIds.length === 1 ? visibleSavedIds[0] : null

    const renderRow = (d) => {
        const name = d.name || d.external_id
        const visible = !hiddenDealerIds.includes(String(d.external_id))
        const isOnly = onlyVisibleId === String(d.external_id)
        return (
            <div className="saved-dealer-item" key={d.id}>
                <div className="saved-dealer-info">
                    <div className="saved-dealer-name">{name}</div>
                    {
                        (d.city || d.state) &&
                        <div className="saved-dealer-loc">
                            {[d.city, d.state].filter(Boolean).join(', ')}
                        </div>
                    }
                </div>
                <div className="saved-dealer-actions">
                    <Tooltip title={`Show only ${name}`} arrow>
                        <button
                            type="button"
                            className={`dealer-only-toggle ${isOnly ? 'active' : ''}`.trim()}
                            onClick={() => toggleShowOnlyDealer(d.external_id)}
                            aria-label={`Show only ${name}`}
                            aria-pressed={isOnly}
                        >
                            <TargetIcon/>
                        </button>
                    </Tooltip>
                    <Tooltip title={visible ? "Click to hide this dealer's listings" : "Click to show this dealer's listings"} arrow>
                        <button
                            type="button"
                            className={`dealer-visibility-toggle ${visible ? 'active' : ''}`.trim()}
                            onClick={() => toggleDealerVisibility(d.external_id)}
                            aria-label={visible ? 'Hide this dealer' : 'Show this dealer'}
                        >
                            <EyeOpenIcon/>
                            <EyeClosedIcon/>
                        </button>
                    </Tooltip>
                </div>
            </div>
        )
    }

    return (
        <div className="right-column">
            <aside className="saved-dealers-panel">
                <div className="saved-dealers-header">
                    <BookmarkIcon fontSize="small"/>
                    <span>Saved Dealers</span>
                    <span className="saved-dealers-count">{saved.length}</span>
                    <Tooltip title="Manage dealerships" arrow>
                        <button
                            type="button"
                            className="saved-dealers-add"
                            onClick={openAddDealerships}
                            aria-label="Manage dealerships"
                        >
                            <SettingsIcon fontSize="small"/>
                        </button>
                    </Tooltip>
                </div>

                {
                    saved.length === 0
                        ? <div className="saved-dealers-empty">
                            No saved dealerships yet — tap the bookmark on a dealership to save it.
                          </div>
                        : <div className="saved-dealers-list">
                            {
                                searchedMake && matching.length === 0 &&
                                <div className="saved-dealers-none-match">
                                    No saved dealers carry {searchedMake}.
                                </div>
                            }
                            {matching.map(renderRow)}

                            {
                                others.length > 0 &&
                                <>
                                    <button
                                        type="button"
                                        className="saved-dealers-others-toggle"
                                        onClick={() => setShowOthers((v) => !v)}
                                    >
                                        {showOthers ? <ExpandMoreIcon fontSize="small"/> : <ChevronRightIcon fontSize="small"/>}
                                        <span>Doesn't carry {searchedMake} ({others.length})</span>
                                    </button>
                                    {showOthers && others.map(renderRow)}
                                </>
                            }
                          </div>
                }
            </aside>
        </div>
    )
}
